import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getTopicArea, deleteTopicArea, getTopicAreaCategories, getTopicAreaTags } from '../api/topicAreaApi';
import { TopicArea, Category, Tag } from '../types';
import { buildCategoryHierarchy } from '../utils/categoryUtils';
import {
  getTopicAreaComments,
  createTopicAreaComment,
  updateTopicAreaComment,
  deleteTopicAreaComment
} from '../api/topicAreaCommentApi';
import { requestRecommend } from '../api/recommendApi';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './TopicAreaDetailPage.css';

interface Comment {
  id: number;
  comment: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  display_name: string;
  parent_id: number | null;
}

const TopicAreaDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [area, setArea] = useState<TopicArea | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [comments, setComments] = useState<Comment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Array<Category & { displayName?: string }>>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingComment, setEditingComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const { user, isAdmin, isTopicAreaOwner } = useAuth();

  const fetchComments = useCallback(async () => {
    if (!id) return;
    try {
      const commentsData = await getTopicAreaComments(Number(id));
      setComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  }, [id]);

  // 실시간 댓글 업데이트 리스너
  useEffect(() => {
    const handleCommentChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { contentType, topicAreaId } = customEvent.detail || {};
      if (contentType === 'topic' && Number(topicAreaId) === Number(id)) {
        fetchComments();
      }
    };

    window.addEventListener('comment_change', handleCommentChange);
    return () => {
      window.removeEventListener('comment_change', handleCommentChange);
    };
  }, [id, fetchComments]);

  // 모든 카테고리 계층 구조 로드
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await buildCategoryHierarchy();
        setAllCategories(categories as Array<Category & { displayName: string }>);
      } catch (error) {
        console.error('카테고리 로딩 실패:', error);
      }
    };
    
    loadCategories();
  }, []);

  useEffect(() => {
    if (id) {
      const topicAreaId = Number(id);
      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);
          const [areaData, commentsData] = await Promise.all([
            getTopicArea(topicAreaId),
            getTopicAreaComments(topicAreaId)
          ]);
          
          setArea(areaData);
          setComments(commentsData);
          
          if (Array.isArray(areaData.categories)) {
            setCategories(areaData.categories);
          } else {
            const categoriesData = await getTopicAreaCategories(topicAreaId);
            setCategories(categoriesData);
          }
          
          setTags(areaData.tags || []);
        } catch (err: any) {
          console.error('Error fetching data:', err);
          const errMsg = err.response?.data?.error || '주제영역을 불러오는 중 오류가 발생했습니다.';
          setError(errMsg);
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteTopicArea(Number(id));
    alert('삭제되었습니다.');
    navigate('/topic-areas');
  };

  const handleRecommendRequest = async () => {
    if (!area) return;
    try {
      await requestRecommend(area.id);
      toast.success('추천 신청이 접수되었습니다. 운영자 검토 후 반영됩니다.');
    } catch (error: any) {
      const errMsg = error.response?.data?.error || '추천 신청에 실패했습니다.';
      toast.error(errMsg);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return toast.warn('댓글을 입력하세요!');
    await createTopicAreaComment(Number(id), newComment, null);
    setNewComment('');
    await fetchComments();
    toast.success('댓글이 등록되었습니다!');
  };

  const handleEditComment = async (commentId: number) => {
    if (!editingComment.trim()) return toast.warn('댓글을 입력하세요!');
    await updateTopicAreaComment(commentId, editingComment);
    setEditingId(null);
    setEditingComment('');
    await fetchComments();
    toast.success('댓글이 수정되었습니다!');
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    await deleteTopicAreaComment(commentId);
    await fetchComments();
    toast.info('댓글이 삭제되었습니다.');
  };

  const handleReply = async (commentId: number) => {
    if (!replyContent.trim() || !user) return;
    
    try {
      await createTopicAreaComment(Number(id), replyContent, commentId);
      setReplyContent('');
      setReplyingTo(null);
      await fetchComments();
      toast.success('답글이 등록되었습니다!');
    } catch (err) {
      console.error('답글 작성 실패:', err);
      toast.error('답글 작성에 실패했습니다.');
    }
  };

  const toggleReplies = (commentId: number) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const renderComment = (comment: Comment, depth: number = 0) => {
    const replies = comments.filter(c => c.parent_id === comment.id);
    const hasReplies = replies.length > 0;
    const isExpanded = expandedComments.has(comment.id);

    return (
      <div key={comment.id} className="comment-node-wrapper" style={{ marginBottom: 16, marginLeft: depth * 24 }}>
        {depth > 0 && <div className="comment-indent-line" />}
        
        <div className="comment-bubble-card">
          <div className="comment-bubble-header">
            <div className="comment-author-box">
              {hasReplies && (
                <button
                  type="button"
                  onClick={() => toggleReplies(comment.id)}
                  className="comment-toggle-replies-btn"
                >
                  <svg 
                    className={`comment-chevron-svg ${isExpanded ? 'expanded' : ''}`} 
                    viewBox="0 0 24 24" 
                    width="14" 
                    height="14"
                  >
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor" />
                  </svg>
                  <span>{replies.length}</span>
                </button>
              )}
              <span className="comment-author-name">
                {comment.display_name || '익명'}
              </span>
              <span className="comment-date">
                {new Date(comment.created_at).toLocaleString()}
                {comment.updated_at && new Date(comment.updated_at) > new Date(comment.created_at) && (
                  <span style={{ marginLeft: 4 }}>(수정됨)</span>
                )}
              </span>
            </div>
            
            <div className="comment-meta-right">
              {user && (user.id === comment.user_id || (user.roles && user.roles.includes('admin'))) && (
                <div className="comment-actions-btns">
                  {editingId === comment.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleEditComment(comment.id)}
                        className="comment-action-btn-item comment-btn-save"
                      >
                        저장
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditingComment('');
                        }}
                        className="comment-action-btn-item comment-btn-cancel"
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(comment.id);
                          setEditingComment(comment.comment);
                        }}
                        className="comment-action-btn-item comment-btn-edit"
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        <span>수정</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="comment-action-btn-item comment-btn-delete"
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        <span>삭제</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {editingId === comment.id ? (
            <textarea
              value={editingComment}
              onChange={(e) => setEditingComment(e.target.value)}
              className="comment-textarea"
            />
          ) : (
            <p className="comment-text-content">
              {comment.comment}
            </p>
          )}
          
          {user && replyingTo !== comment.id && (
            <div>
              <button
                type="button"
                onClick={() => setReplyingTo(comment.id)}
                className="comment-reply-trigger-btn"
              >
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 17 4 12 9 7"></polyline>
                  <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                </svg>
                <span>답글 쓰기</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Render reply form if this is the comment being replied to */}
        {replyingTo === comment.id && (
          <div className="reply-form-box">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="답글을 입력하세요..."
              className="comment-textarea"
              style={{ minHeight: 60, marginBottom: 8 }}
            />
            <div className="reply-actions-row">
              <button
                type="button"
                onClick={() => handleReply(comment.id)}
                disabled={!replyContent.trim()}
                className="comment-action-btn-item reply-submit-btn"
                style={{ opacity: replyContent.trim() ? 1 : 0.5 }}
              >
                답글 작성
              </button>
              <button
                type="button"
                onClick={() => {
                  setReplyingTo(null);
                  setReplyContent('');
                }}
                className="comment-action-btn-item reply-cancel-btn"
              >
                취소
              </button>
            </div>
          </div>
        )}
        
        {/* Render replies if expanded */}
        {hasReplies && isExpanded && (
          <div style={{ marginTop: 12 }}>
            {replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontWeight: 'bold' }}>Loading...</div>;
  
  if (error) {
    return (
      <div style={{ padding: '80px 40px', textAlign: 'center' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
        </svg>
        <h2 style={{ color: 'var(--danger)', marginBottom: '8px' }}>접근 권한 제한</h2>
        <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px 0' }}>{error}</p>
        <Link to="/topic-areas" className="modern-btn btn-list" style={{ display: 'inline-block', textDecoration: 'none' }}>
          목록으로 돌아가기
        </Link>
      </div>
    );
  }

  if (!area) return <div style={{ padding: 40, textAlign: 'center' }}>주제영역을 찾을 수 없습니다.</div>;

  return (
    <div className="topic-detail-container">
      <h1>{area.title}</h1>
      <p className="topic-detail-summary">{area.summary}</p>
      
      {/* 정보 메타 카드 그리드 */}
      <div className="info-section-grid">
        {/* 카테고리 정보 표시 */}
        <div className="info-detail-card">
          <h3 className="topic-section-title">📂 카테고리 정보</h3>
          <div className="info-card-row">
            <span className="info-card-label">주요 카테고리 1</span>
            <span className="info-card-value">
              {area.main_category1_id ? 
                (allCategories.find(c => c.id === area.main_category1_id)?.displayName || `ID: ${area.main_category1_id}`) : 
                'N/A'}
            </span>
          </div>
          <div className="info-card-row">
            <span className="info-card-label">주요 카테고리 2</span>
            <span className="info-card-value">
              {area.main_category2_id ? 
                (allCategories.find(c => c.id === area.main_category2_id)?.displayName || `ID: ${area.main_category2_id}`) : 
                'N/A'}
            </span>
          </div>
          <div className="info-card-row">
            <span className="info-card-label">주요 카테고리 3</span>
            <span className="info-card-value">
              {area.main_category3_id ? 
                (allCategories.find(c => c.id === area.main_category3_id)?.displayName || `ID: ${area.main_category3_id}`) : 
                'N/A'}
            </span>
          </div>
        </div>

        {/* 기간 정보 표시 */}
        <div className="info-detail-card">
          <h3 className="topic-section-title">⏱️ 기간 정보</h3>
          <div className="info-card-row">
            <span className="info-card-label">시작년월</span>
            <span className="info-card-value">
              {area.start_year}년 {area.start_month ? `${area.start_month}월` : ''}
            </span>
          </div>
          <div className="info-card-row">
            <span className="info-card-label">종료년월</span>
            <span className="info-card-value">
              {area.is_current === 'Y' ? '현재 진행 중' : 
                `${area.end_year}년 ${area.end_month ? `${area.end_month}월` : ''}`}
            </span>
          </div>
        </div>
      </div>

      {/* 카테고리 태그 칩스 */}
      {categories.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 className="topic-section-title">📁 연결된 세부 카테고리</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {categories.map(category => (
              <span key={category.id} className="badge badge-primary" style={{ fontSize: 13, padding: '6px 14px' }}>
                📂 {category.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 태그 표시 */}
      {tags.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 className="topic-section-title">🏷️ 태그 마스터</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tags.map(tag => (
              <span key={tag.id} className="badge badge-accent" style={{ fontSize: 13, padding: '6px 14px' }}>
                🏷️ {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 상세 내용 */}
      <div className="detail-content-box">
        <h3>📄 상세 내용</h3>
        <div className="detail-content-text">{area.content}</div>
      </div>

      {/* 메타 등록 정보 */}
      <div className="meta-timestamp-box">
        <span>
          최초 등록: {new Date(area.created_at).toLocaleString()}
          {area.creator_username && ` (소유자: ${area.creator_nickname || area.creator_username})`}
        </span>
        <span>
          최종 수정: {new Date(area.updated_at).toLocaleString()}
          {area.updater_username && ` (수정자: ${area.updater_nickname || area.updater_username})`}
        </span>
      </div>

      {/* 액션 버튼 - 좌/우 분리 */}
      <div className="detail-actions detail-actions-split">
        <div className="detail-actions-left">
          {user && (isAdmin() || isTopicAreaOwner(area.created_by)) && (
            <>
              <button onClick={() => navigate(`/topic-areas/${area.id}/edit`)} className="modern-btn btn-edit">
                수정
              </button>
              <button onClick={handleDelete} className="modern-btn btn-delete">
                삭제
              </button>
            </>
          )}
          <Link to="/topic-areas" className="modern-btn btn-list">
            목록으로
          </Link>
        </div>
        <div className="detail-actions-right">
          {user && (
            <>
              {(isAdmin() || isTopicAreaOwner(area.created_by)) && (
                <>
                  <button
                    onClick={() => navigate(`/topic-areas/${area.id}/raw-import`)}
                    className="modern-btn btn-raw-import"
                  >
                    비정형기록등록
                  </button>
                  <button
                    onClick={() => navigate(`/content/create?topicAreaId=${area.id}`)}
                    className="modern-btn btn-record-create"
                  >
                    📝 기록등록
                  </button>
                </>
              )}
              {area.is_public === 'Y' && (
                <button
                  onClick={() => navigate(`/archives?topicAreaId=${area.id}`)}
                  className="modern-btn btn-archive-list"
                >
                  기록 아카이브
                </button>
              )}
              {(isAdmin() || isTopicAreaOwner(area.created_by)) && (
                <button
                  onClick={handleRecommendRequest}
                  className="modern-btn btn-recommend-request"
                >
                  ⭐ 추천신청
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 타임라인 연계 링크 배너 */}
      {area.is_public === 'Y' && (
        <div className="view-timeline-link-box">
          <Link to={`/timeline/${area.id}`}>
            📅 이 주제영역에 등록된 사건 타임라인 모아보기 <span>→</span>
          </Link>
        </div>
      )}

      {/* 댓글 영역 */}
      <div className="comments-container">
        <h3 className="comments-title">💬 토론 및 의견 ({comments.filter(c => !c.parent_id).length}개)</h3>
        
        {/* 댓글 작성 폼 */}
        <form onSubmit={(e) => { e.preventDefault(); handleAddComment(); }} className="comment-form">
          <div className="comment-textarea-wrapper">
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder={user ? "역사적 사실에 대한 소중한 의견을 작성해주세요..." : "댓글 작성을 위해 먼저 로그인을 진행해주세요."}
              disabled={!user}
              className="comment-textarea"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              disabled={!user || !newComment.trim()}
              className="comment-submit-btn"
              style={{
                backgroundColor: user && newComment.trim() ? 'var(--primary)' : 'var(--text-tertiary)',
                color: 'white',
                cursor: user && newComment.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              의견 등록
            </button>
          </div>
        </form>

        {/* 댓글 목록 */}
        <div className="comment-list-wrapper">
          {comments.filter(comment => !comment.parent_id).map(comment => renderComment(comment))}
          
          {comments.length === 0 && (
            <div className="comments-empty-state">
              아직 등록된 의견이 없습니다. 첫 번째 토론 글을 남겨보세요!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicAreaDetailPage;
